import React, { useState, useEffect, useRef, useImperativeHandle, forwardRef } from "react";
import PropTypes from "prop-types";
import { Form, Input, Tooltip, Button, Flex, Alert } from "antd";
import { Title, Text } from "ui/Typography";
import { Icon } from "ui/Icon";
import { CheckableTag } from "ui/CheckableTag";
import { ConfirmModal } from "ui/ConfirmModal";
import { useTranslation } from "react-i18next";
import { textGenerate } from "store/text-generate";
import { useDispatch, useSelector } from "react-redux";
import { getPromptList } from "store/guide-profile";
import styles from "./index.module.css"
import { AI_TAGS, LIST_FOR_MAP } from "./constants";
import DOMPurify from "dompurify";
const { TextArea } = Input
const MIN_CHARACTER_LENTGTH = 80

const AiEnhancementFormInner = ({ description, setDescription, setIsSaved, setProfileDescription, setAiDescriptionData, text, messages, refinementTooltipTitle, improveWithAITooltipTitle }, ref) => {
  const [form] = Form.useForm()
  const [tagError, setTagError] = useState("");
  const dispatch = useDispatch()
  const initialDescription = useRef(description)
  const initialTags = useRef([])
  const [displayText, setDisplayText] = useState("")
  // Expose unsaved changes check to parent
  useImperativeHandle(ref, () => ({
    hasUnsavedChanges: () => {
      const currentPrompt = form.getFieldValue('profilePrompt') || ''
      const initialPrompt = initialDescription.current || ''
      const tagsChanged = selectedTags.length !== initialTags.current.length || selectedTags.some((tag, i) => tag !== initialTags.current[i])
      return currentPrompt.trim() !== initialPrompt.trim() || tagsChanged
    }
  }))
  const promptList = useSelector((store) => store.guideProfile.promptList.data);
  useEffect(() => {
    // Fetch prompt list on mount with list_for from LIST_FOR_MAP
    const listFor = LIST_FOR_MAP[text] || 'profile';
    dispatch(getPromptList({ list_for: listFor }));
  }, [dispatch, text]);
  const [selectedTags, setSelectedTags] = useState([])
  const { t } = useTranslation()
  const [isConfirmOpened, setIsConfirmOpened] = React.useState(false)
  const [isAiResponse, setisAiResponse] = useState(null)
  const [isAiLoading, setisAiLoading] = useState(false)
  const handleTagChange = (tag, checked) => {
    let nextSelectedTags = [...selectedTags];
    if (checked) {
      nextSelectedTags.push(tag);
      if (tag === AI_TAGS.MAKE_IT_SHORTER) {
        nextSelectedTags = nextSelectedTags.filter(t => t !== AI_TAGS.MAKE_IT_LONGER);
      }
      if (tag === AI_TAGS.MAKE_IT_LONGER) {
        nextSelectedTags = nextSelectedTags.filter(t => t !== AI_TAGS.MAKE_IT_SHORTER);
      }
      if (tag === AI_TAGS.JUST_FIX_GRAMMAR) {
        nextSelectedTags = [AI_TAGS.JUST_FIX_GRAMMAR];
      }
      if (tag === AI_TAGS.MAKE_IT_MORE_CASUAL) {
        nextSelectedTags = nextSelectedTags.filter(t => t !== AI_TAGS.MAKE_IT_MORE_PROFESSIONAL);
      }
      if (tag === AI_TAGS.MAKE_IT_MORE_PROFESSIONAL) {
        nextSelectedTags = nextSelectedTags.filter(t => t !== AI_TAGS.MAKE_IT_MORE_CASUAL);
      }
    } else {
      nextSelectedTags = nextSelectedTags.filter(t => t !== tag);
    }
    setSelectedTags(nextSelectedTags);
    if (nextSelectedTags.length > 0) {
      setTagError("");
    }
  }

  // Convert HTML to plain text appropriate for TextArea
  const htmlToPlainText = (html) => {
    const sanitized = DOMPurify.sanitize(html || "");
    const converted = sanitized
      .replace(/<\s*br\s*\/?\s*>/gi, "\n")
      // .replace(/<\s*\/(?:p|div|li|h[1-6]|blockquote)\s*>/gi, "\n")
      .replace(/<\s*(?:p|div|h[1-6]|blockquote)(?:\s+[^>]*)?>/gi, "\n")
      .replace(/<\s*li\s*>/gi, "- ")
      .replace(/<\s*\/ul\s*>/gi, "\n")
      .replace(/<\s*\/ol\s*>/gi, "\n");
    const temp = document.createElement("div");
    temp.innerHTML = converted;
    const text = (temp.textContent || "")
      .replace(/\u00A0/g, " ")
      .replace(/\r\n?/g, "\n")
    // .replace(/\n{3,}/g, "\n\n");
    return text;
  };

  React.useEffect(() => {
    let value = description;
    if (isAiLoading) {
      value = ""
    } else if (isAiResponse?.paragraph_text) {
      value = isAiResponse?.paragraph_text
    }
    // Normalize for textarea display and form field
    const normalized = htmlToPlainText(value)
    setDisplayText(normalized)
    setAiDescriptionData(text === 'Email Content' || text === 'Message Content' || text === 'Text Content' ? normalized?.replace(/\n/g, '<br/>') : normalized)
    form.setFieldsValue({ profilePrompt: normalized });
    initialDescription.current = description;
    initialTags.current = [];
  }, [description, isAiLoading, isAiResponse, form]);
  const validateTextArea = (_, value) => {
    if (!value || value.trim().length <= MIN_CHARACTER_LENTGTH) {
      return Promise.reject(new Error(t("pro.ai_enhancement.form.text_too_short")))
    }
    return Promise.resolve()
  }

  const handleRewrite = async () => {
    // Check if at least one tag is selected
    if (selectedTags.length === 0) {
      setTagError(t("pro.ai_enhancement.error_message"));
      return;
    } else {
      setTagError("");
    }
    const values = form.getFieldsValue()
    setisAiLoading(true)
    // Call the textGenerate API with the form values and selectedTags
    dispatch(textGenerate({
      content: values.profilePrompt,
      prompt: selectedTags
    })).then(res => {
      setisAiResponse(res)
      setIsSaved(false)
    }).finally(() => {
      setisAiLoading(false)
    })
  }
  const onValuesChange = (_, allValues) => {
    // Removed useless assignment to isTextValid
  }

  const onStartOverClick = () => {
    setIsConfirmOpened(true)
  }

  const onConfirm = () => {
    setProfileDescription('')
    handleStartOver()
    setIsConfirmOpened(false)
  }
  const handleStartOver = () => {
    form.resetFields()
    setSelectedTags([])
    setisAiLoading(false)
    setisAiResponse(null)
    setIsSaved(true);
  }

  // Extracted label logic for clarity
  let profilePromptLabel;
  if (isAiLoading) {
    profilePromptLabel = (
      <Flex wrap align="center">
        <Title level={4} className="mb-0 ml-xs">
          {t("pro.ai_enhancement.form.ai_enhanced_label")}
        </Title>
      </Flex>
    );
  } else if (isAiResponse) {
    profilePromptLabel = (
      <Flex wrap align="center" gap={4}>
        <Title level={4} className="mb-0 ml-xs">
          {t("pro.ai_enhancement.form.ai_enhanced_label")}
        </Title>
        <Icon name="checkedCircle" height={20} width={20} />
        <Text type="body" className="mb-0 ml-xxs">{t("pro.ai_enhancement.form.ai_enhanced_description_label")}</Text>
      </Flex>
    );
  } else {
    profilePromptLabel = null;
  }

  // Now return the JSX
  return (
    <>
      <Form
        form={form}
        layout="vertical"
        onValuesChange={onValuesChange}
        initialValues={{ profilePrompt: description }}
      >
        <Flex gap="small" align="center" justify="space-between">
          <Flex wrap gap="small" align="center">
            <Title level={4} className="mb-0">
              {text}
            </Title>
            <Tooltip title={"Prompt text needs to be added"} placement="bottom">
              <Icon name="AlertCircle" width={20} height={20} className='cursor-pointer' />
            </Tooltip>
          </Flex>
          {(isAiLoading || isAiResponse) && (
            <Button color="primary" variant="outlined" className="mb-xs" onClick={onStartOverClick} disabled={!isAiResponse}>
              {t("pro.ai_enhancement.form.start_over")}
            </Button>
          )}
        </Flex>

        {(isAiResponse || isAiLoading) && (
          <Alert
            className={`mb-xs ${styles.descriptionAlert} ${description?.length > 300 ? styles.fixedHeight : ''}`}
            message={
              <span dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(description) }}
              ></span>
            }
            type="info"
          />
        )}
        <Form.Item
          name="profilePrompt"
          label={profilePromptLabel}
          rules={[{ validator: validateTextArea }]}
        >{
            isAiLoading && (
              <Flex gap={5} className={styles.loadingContainer}>
                <div className={styles.loadingDot} />
                <div className={`${styles.loadingDot} ${styles.loadingDotAnimation}`} />
                <div className={styles.loadingDot} />
              </Flex>
            )
          }
          <TextArea
            rows={4}
            value={displayText}
            placeholder={!isAiLoading && !isAiResponse ? "Enter your profile prompt" : ""}
            onChange={e => {
              const val = e.target.value
              if (!isAiLoading && !isAiResponse) {
                setDisplayText(val)
                form.setFieldsValue({ profilePrompt: val })
              }
            }}
          />
        </Form.Item>
        {
          isAiResponse && (
            <Alert
              className="mb-xs"
              icon={
                <Tooltip title={refinementTooltipTitle}
                  placement="bottom">
                  <Icon name="AlertCircle" width={20} height={20} className='cursor-pointer' />
                </Tooltip>
              }
              message={messages}
              type="info"
              showIcon
              closable
            />
          )
        }
        <Form.Item
          label={
            <Flex wrap gap="small" align="center" className="mb-xs">
              <Text type="subtitle">{t("pro.ai_enhancement.form.ai_label")}</Text>
              <Tooltip title={improveWithAITooltipTitle}
                placement="bottom">
                <Icon name="AlertCircle" width={20} height={20} className='cursor-pointer' />
              </Tooltip>
            </Flex>
          }
        >
          <Flex wrap gap="small">
            {promptList?.prompts?.map?.((tag) => (
              <CheckableTag tag={tag} checked={selectedTags.includes(tag)} onChange={(checked) => handleTagChange(tag, checked)} />
            ))}
          </Flex>
          {tagError && (
            <Text type="danger" style={{ color: 'red', marginTop: 8 }}>{tagError}</Text>
          )}
        </Form.Item>

        <Form.Item>
          <Button
            type="primary"
            icon={<Icon name="aiEnhancement" color={isAiLoading ? "#000000" : "#ffffff"} height={20} width={20} />}
            onClick={handleRewrite}
            disabled={isAiLoading}
          >
            {t("pro.ai_enhancement.form.button_text")}
          </Button>
        </Form.Item>
      </Form>
      <ConfirmModal
        title={t("pro.ai_enhancement.form.start_over_confirm_title")}
        info={t("pro.ai_enhancement.form.start_over_confirm_info")}
        cancelLabel={t("pro.ai_enhancement.close_modal_confirm_cancel")}
        okLabel={t("pro.ai_enhancement.form.start_over_confirm_ok_text")}
        open={isConfirmOpened}
        onCancel={() => setIsConfirmOpened(false)}
        onOk={onConfirm}
      />
    </>
  )
}

export const AiEnhancementForm = forwardRef(AiEnhancementFormInner);
AiEnhancementFormInner.propTypes = {
  description: PropTypes.string,
  setDescription: PropTypes.func,
  setProfileDescription: PropTypes.func,
  setAiDescriptionData: PropTypes.func,
  text: PropTypes.string,
  messages: PropTypes.string,
  refinementTooltipTitle: PropTypes.string,
  improveWithAITooltipTitle: PropTypes.string,
  setIsSaved: PropTypes.func
}
